import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { RootState } from 'src/redux/store';
import { ProductRequest, ProductResponse, ProductsClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';

export interface Product extends ProductResponse { }

export interface State {
    isLoading: boolean;
    error: boolean;
    productList: Product[];
    selectedProduct?: Product;
}

const initialState: State = {
    isLoading: false,
    error: false,
    productList: [],
};

const productSlice = createSlice({
    name: 'product',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getProductListSuccess: (state, action) => {
            state.productList = action.payload;
            state.isLoading = false;
        },
        getProductSuccess: (state, action: PayloadAction<Product | undefined>) => {
            state.selectedProduct = action.payload;
            state.isLoading = false;
        },
        addNewProductSuccess: (state, action: PayloadAction<Product>) => {
            const newProduct = action.payload;
            state.productList = [newProduct, ...state.productList];
            state.isLoading = false;
        },
        updateProductSuccess: (state, action: PayloadAction<Product>) => {
            const updatedItem = action.payload;
            const index = state.productList.findIndex(item => item.productId === updatedItem.productId);
            state.productList = [...state.productList.slice(0, index), updatedItem, ...state.productList.slice(index + 1)];
            state.isLoading = false;
        },
    },
});

// Reducer
export default productSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
    getProductListSuccess,
    getProductSuccess,
    addNewProductSuccess,
    updateProductSuccess,
} = productSlice.actions;

// for useSelector
export const selectProduct = (state: RootState) => state.product as State;

// ----------------------------------------------------------------------
export function getAllProducts(ignoreCache: boolean = false) {
    return async (dispatch, getState) => {
        try {
            const { productList } = selectProduct(getState());
            if (ignoreCache || productList.length === 0) {
                dispatch(productSlice.actions.startLoading());
                const client = new ProductsClient(undefined, axios);
                const response = await client.getAll(false);
                dispatch(productSlice.actions.getProductListSuccess(response));
            }
        } catch (error) {
            dispatch(productSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function getProduct(id: number) {
    return async (dispatch, getState) => {
        if (id > 0) {
            dispatch(productSlice.actions.startLoading());
            try {
                const client = new ProductsClient(undefined, axios);
                const response = await client.get(id);
                dispatch(productSlice.actions.getProductSuccess(response));
            } catch (error) {
                dispatch(productSlice.actions.hasError(true));
                handleError(error);
            }
        } else {
            dispatch(productSlice.actions.getProductSuccess(undefined));
        }
    };
}

export function createProduct(product: Product, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(productSlice.actions.startLoading());
        try {
            const { productId, ...payload } = product;
            const request = payload as ProductRequest;
            const client = new ProductsClient(undefined, axios);
            const response = await client.create(request);
            dispatch(productSlice.actions.addNewProductSuccess(response));
            if (showNotification) {
                toast.success(`${response.productName} created successfully!`);
            }
        } catch (error) {
            dispatch(productSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function updateProduct(product: Product, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(productSlice.actions.startLoading());
        try {
            const { productId, oldProductNumber, ...payload } = product;
            const request = payload as ProductRequest;
            const client = new ProductsClient(undefined, axios);
            const response = await client.edit(productId, request);
            dispatch(productSlice.actions.updateProductSuccess(response));
            if (showNotification) {
                toast.success(`${response.productName} ${response.isActive ? 'updated' : 'deleted'} successfully!`);
            }
        } catch (error) {
            dispatch(productSlice.actions.hasError(true));
            handleError(error);
        }
    };
}